import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  updateDoc, 
  query, 
  where, 
  setDoc,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { User, EventCode } from '../types/alarm';
import { GroupCouponBatch } from '../types/license';

/** 관리자 통계 세그먼트별 사용자 한 명 (listUsersForStatsSegment 응답) */
export interface AdminStatsListMember {
  id: string;
  name: string;
  email: string;
  userType: string;
  planSegment: 'individualPaid' | 'groupCoupon' | 'eventCoupon' | null;
  planTypeLabel: string;
  planPeriodLabel: string;
  licenses: Record<string, unknown>[];
}

/** 브라우저 localStorage 캐시 (재조회 시 Functions·Firestore 읽기 없음). TTL 초과 시 자동 무효 */
const ADMIN_STATS_LIST_CACHE_PREFIX = 'luckytemAdminStatsList:v3:';
const ADMIN_STATS_LIST_CACHE_TTL_MS = 30 * 60 * 1000; // 30분

interface AdminStatsListCachePayload {
  savedAt: number;
  members: AdminStatsListMember[];
}

function normalizeAdminStatsListMember(member: AdminStatsListMember): AdminStatsListMember {
  const rawPeriod = typeof member.planPeriodLabel === 'string' ? member.planPeriodLabel.trim() : '';
  return {
    ...member,
    planPeriodLabel: rawPeriod && rawPeriod !== '-' ? rawPeriod : '이용권 이용 기간 없음',
  };
}

function normalizeAdminStatsListMembers(members: AdminStatsListMember[]): AdminStatsListMember[] {
  return members.map(normalizeAdminStatsListMember);
}

function readAdminStatsListLocalCache(segment: string): AdminStatsListMember[] | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(ADMIN_STATS_LIST_CACHE_PREFIX + segment);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminStatsListCachePayload;
    if (!parsed || !Array.isArray(parsed.members) || typeof parsed.savedAt !== 'number') {
      window.localStorage.removeItem(ADMIN_STATS_LIST_CACHE_PREFIX + segment);
      return null;
    }
    if (Date.now() - parsed.savedAt > ADMIN_STATS_LIST_CACHE_TTL_MS) {
      window.localStorage.removeItem(ADMIN_STATS_LIST_CACHE_PREFIX + segment);
      return null;
    }
    return normalizeAdminStatsListMembers(parsed.members);
  } catch {
    try {
      window.localStorage.removeItem(ADMIN_STATS_LIST_CACHE_PREFIX + segment);
    } catch {
      /* ignore */
    }
    return null;
  }
}

function writeAdminStatsListLocalCache(segment: string, members: AdminStatsListMember[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const payload: AdminStatsListCachePayload = {
      savedAt: Date.now(),
      members: normalizeAdminStatsListMembers(members),
    };
    const key = ADMIN_STATS_LIST_CACHE_PREFIX + segment;
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {
    console.warn('통계 목록 localStorage 캐시 저장 실패(용량 등):', e);
  }
}

/** 집계 재계산·강제 새로고침 전에 호출해 세그먼트 목록 캐시 제거 */
export function clearAdminStatsListLocalCache(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(ADMIN_STATS_LIST_CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

const EVENT_CODES_COLLECTION = 'eventCodes';
const GROUP_COUPON_CODES_COLLECTION = 'groupCouponCodes';

const mapGroupCouponBatch = (docId: string, data: any): GroupCouponBatch => ({
  batchId: docId,
  modelVersion: data.modelVersion,
  groupCode: data.groupCode || docId,
  buyerUid: data.buyerUid || '',
  buyerEmail: data.buyerEmail,
  groupName: data.groupName || '',
  targetEmail: data.targetEmail || '',
  planType: data.planType || 'basic',
  termMonths: data.termMonths || 1,
  quantity: data.quantity || 0,
  unitPriceKrw: data.unitPriceKrw || 0,
  totalAmountKrw: data.totalAmountKrw || 0,
  paymentId: data.paymentId || '',
  status: data.status || 'ISSUED',
  issuedCount: data.issuedCount || 0,
  activatedCount: data.activatedCount || 0,
  refundedCount: data.refundedCount || 0,
  createdAt: (data.createdAt as any)?.toDate ? (data.createdAt as any).toDate() : new Date(),
  activationValidUntilYmd: data.activationValidUntilYmd,
  refundEligibleUntilYmd: data.refundEligibleUntilYmd || '',
  refundedAt: (data.refundedAt as any)?.toDate ? (data.refundedAt as any).toDate() : undefined,
  refundedAmountKrw: data.refundedAmountKrw || 0,
  refundKrw: data.refundKrw || 0,
  unusedRefundKrw: data.unusedRefundKrw || 0,
  activatedRefundKrw: data.activatedRefundKrw || 0,
  totalRefundKrw: data.totalRefundKrw || 0,
  activatedUserUids: Array.isArray(data.activatedUserUids) ? data.activatedUserUids : [],
  emailStatus: data.emailStatus || 'PENDING',
  retryCount: data.retryCount || 0,
  lastError: data.lastError,
  lastEmailSentAt: (data.lastEmailSentAt as any)?.toDate ? (data.lastEmailSentAt as any).toDate() : undefined,
  lastTriedAt: (data.lastTriedAt as any)?.toDate ? (data.lastTriedAt as any).toDate() : undefined,
  paymentMode: data.paymentMode,
});

export const upgradeService = {
  /** 동기: TTL 유효한 세그먼트 목록 캐시만 반환 (UI 즉시 표시용) */
  tryReadAdminStatsListLocalCache(segment: string): AdminStatsListMember[] | null {
    return readAdminStatsListLocalCache(segment);
  },

  // 관리자 권한 확인
  async checkAdminPermission(userId: string): Promise<boolean> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return false;
      }
      const userData = userDoc.data();
      return userData?.userType === 'admin';
    } catch (error) {
      console.error('관리자 권한 확인 오류:', error);
      return false;
    }
  },

  // 코드 검증 및 업그레이드 (이벤트 코드만 사용)
  async validateAndUpgradeUser(
    userId: string, 
    code: string,
    options?: { forceApplyExistingLicense?: boolean }
  ): Promise<{ success: boolean; newUserType?: 'basic' | 'standard' | 'pro'; error?: string; requireExistingLicenseConfirmation?: boolean }> {
    if (!code || !code.trim()) {
      return { success: false, error: '코드가 필요합니다.' };
    }
    const normalized = code.trim().toUpperCase();
    if (normalized.startsWith('GRP-')) {
      return this.validateAndActivateGroupCoupon(userId, normalized, options);
    }
    return this.validateAndActivateEventCode(userId, normalized);
  },

  // 이벤트 코드 검증 및 활성화 (서버 Callable 호출: eventCodes 쓰기 권한이 서버에만 있음. 이용권도 서버에서 생성)
  async validateAndActivateEventCode(
    _userId: string,
    code: string
  ): Promise<{ success: boolean; newUserType?: 'basic' | 'standard' | 'pro'; license?: { planId: string; planType: string; duration: number; purchasedAt: Date; expiresAt: Date; isActive: boolean; paymentId: string; amount: number; currency: string }; error?: string }> {
    try {
      const activateEventCodeFn = httpsCallable<
        { code: string },
        { success: boolean; newUserType?: 'basic' | 'standard' | 'pro'; license?: { planId: string; planType: string; duration: number; purchasedAt: Date; expiresAt: Date; isActive: boolean; paymentId: string; amount: number; currency: string }; error?: string }
      >(functions, 'activateEventCode');
      const res = await activateEventCodeFn({ code });
      const data = res.data as any;
      if (data?.success && data.newUserType) {
        return { success: true, newUserType: data.newUserType, license: data.license };
      }
      return { success: false, error: data?.error || '이벤트 코드 활성화에 실패했습니다.' };
    } catch (error: any) {
      const msg = error?.message || error?.code || '이벤트 코드 활성화에 실패했습니다.';
      const errMessage = typeof msg === 'string' ? msg : (error?.message || '이벤트 코드 활성화에 실패했습니다.');
      console.error('이벤트 코드 활성화 실패:', error);
      return { success: false, error: errMessage };
    }
  },

  // 그룹 쿠폰 검증 및 활성화 (서버 Callable 호출)
  async validateAndActivateGroupCoupon(
    _userId: string,
    code: string,
    options?: { forceApplyExistingLicense?: boolean }
  ): Promise<{ success: boolean; newUserType?: 'basic' | 'standard' | 'pro'; license?: { planId: string; planType: string; duration: number; purchasedAt: Date; expiresAt: Date; isActive: boolean; paymentId: string; amount: number; currency: string; groupBatchId?: string }; error?: string; requireExistingLicenseConfirmation?: boolean }> {
    try {
      const activateGroupCouponFn = httpsCallable<
        { code: string; forceApplyExistingLicense?: boolean },
        { success: boolean; newUserType?: 'basic' | 'standard' | 'pro'; license?: { planId: string; planType: string; duration: number; purchasedAt: Date; expiresAt: Date; isActive: boolean; paymentId: string; amount: number; currency: string; groupBatchId?: string }; error?: string; requireExistingLicenseConfirmation?: boolean }
      >(functions, 'activateGroupCoupon');
      const res = await activateGroupCouponFn({ code, forceApplyExistingLicense: options?.forceApplyExistingLicense === true });
      const data = res.data as any;
      if (data?.success && data.newUserType) {
        return { success: true, newUserType: data.newUserType, license: data.license };
      }
      return {
        success: false,
        error: data?.error || '그룹 쿠폰 활성화에 실패했습니다.',
        requireExistingLicenseConfirmation: data?.requireExistingLicenseConfirmation === true,
      };
    } catch (error: any) {
      const msg = error?.message || error?.code || '그룹 쿠폰 활성화에 실패했습니다.';
      const errMessage = typeof msg === 'string' ? msg : (error?.message || '그룹 쿠폰 활성화에 실패했습니다.');
      console.error('그룹 쿠폰 활성화 실패:', error);
      return { success: false, error: errMessage };
    }
  },

  // 사용자 권한 검증 (로그인 시 호출, 이벤트 코드만 사용)
  async validateUserPermissions(user: User): Promise<'free' | 'basic' | 'standard' | 'pro' | 'admin'> {
    try {
      const upgradeCode = user.upgradeCode;
      if (!upgradeCode) {
        return user.userType;
      }

      const eventRef = doc(db, EVENT_CODES_COLLECTION, upgradeCode.code);
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, { userType: 'free', upgradeCode: null });
        console.log('권한 복원:', { userId: user.id, reason: 'code_not_found' });
        return 'free';
      }
      
      const eventData = eventDoc.data() as EventCode;
      const activatedUsers = eventData.activatedUsers || [];
      if (!activatedUsers.includes(user.id)) {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, { userType: 'free', upgradeCode: null });
        console.log('권한 복원:', { userId: user.id, reason: 'invalid_code' });
        return 'free';
      }

      const usedAtRaw: any = upgradeCode.usedAt as any;
      const usedAt = usedAtRaw?.toDate ? usedAtRaw.toDate() : usedAtRaw instanceof Date ? usedAtRaw : null;
      if (usedAt && eventData.durationMonths) {
        const expiresAt = new Date(usedAt);
        expiresAt.setMonth(expiresAt.getMonth() + eventData.durationMonths);
        if (expiresAt.getTime() <= Date.now()) {
          const userRef = doc(db, 'users', user.id);
          await updateDoc(userRef, { userType: 'free', upgradeCode: null });
          return 'free';
        }
      }
      return upgradeCode.targetUserType;
    } catch (error) {
      console.error('권한 검증 실패:', error);
      return 'free';
    }
  },

  // 관리자 통계 조회 (집계 문서 adminStats/users 1회 읽기, 없으면 기존 방식 폴백)
  async getAdminStats(adminUserId: string): Promise<{
    success: boolean;
    data?: {
      userStats: {
        total: number;
        free: number;
        basic: number;
        standard: number;
        pro: number;
        admin: number;
        basicIndividualPaid?: number;
        basicGroupCoupon?: number;
        basicEventCoupon?: number;
        standardIndividualPaid?: number;
        standardGroupCoupon?: number;
        standardEventCoupon?: number;
        proIndividualPaid?: number;
        proGroupCoupon?: number;
        proEventCoupon?: number;
      };
      lastUpdated: Date;
    };
    error?: string;
  }> {
    try {
      // 관리자 권한 확인
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) {
        return { success: false, error: '관리자 권한이 필요합니다.' };
      }

      // 사용자 통계: 집계 문서 우선 읽기 (1 read)
      const statsRef = doc(db, 'adminStats', 'users');
      const statsSnap = await getDoc(statsRef);
      let userStats: {
        total: number; free: number; basic: number; standard: number; pro: number; admin: number;
        basicIndividualPaid: number; basicGroupCoupon: number; basicEventCoupon: number;
        standardIndividualPaid: number; standardGroupCoupon: number; standardEventCoupon: number;
        proIndividualPaid: number; proGroupCoupon: number; proEventCoupon: number;
      };
      let lastUpdated: Date;

      const buildStatsByScanningUsers = async () => {
        const { getPlanSegmentFromUserData } = await import('../utils/licenseDisplay');
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const nextStats = {
          total: 0,
          free: 0,
          basic: 0,
          standard: 0,
          pro: 0,
          admin: 0,
          basicIndividualPaid: 0,
          basicGroupCoupon: 0,
          basicEventCoupon: 0,
          standardIndividualPaid: 0,
          standardGroupCoupon: 0,
          standardEventCoupon: 0,
          proIndividualPaid: 0,
          proGroupCoupon: 0,
          proEventCoupon: 0,
        };
        usersSnapshot.docs.forEach(docSnap => {
          const userData = docSnap.data();
          nextStats.total++;
          const userType = (userData.userType || 'free') as string;
          nextStats[userType as keyof typeof nextStats]++;
          const segment = getPlanSegmentFromUserData(userData);
          if (segment && (userType === 'basic' || userType === 'standard' || userType === 'pro')) {
            const suffix =
              segment === 'individualPaid' ? 'IndividualPaid'
              : segment === 'groupCoupon' ? 'GroupCoupon'
              : 'EventCoupon';
            const key = `${userType}${suffix}` as keyof typeof nextStats;
            nextStats[key]++;
          }
        });
        return nextStats;
      };

      /** 집계 문서에 새 키는 있으나 값이 0으로만 남은 등, 플랜 합계와 3분류 합이 어긋나면 스캔으로 보정 */
      const planBreakdownMismatch = (s: typeof userStats): boolean => {
        for (const plan of ['basic', 'standard', 'pro'] as const) {
          const total = s[plan];
          const ind = s[`${plan}IndividualPaid`];
          const grp = s[`${plan}GroupCoupon`];
          const evt = s[`${plan}EventCoupon`];
          if (total !== ind + grp + evt) return true;
        }
        return false;
      };

      if (statsSnap.exists()) {
        const d = statsSnap.data() as Record<string, number | { toDate?: () => Date }>;
        const breakdownKeys = [
          'basicIndividualPaid',
          'basicGroupCoupon',
          'basicEventCoupon',
          'standardIndividualPaid',
          'standardGroupCoupon',
          'standardEventCoupon',
          'proIndividualPaid',
          'proGroupCoupon',
          'proEventCoupon',
        ] as const;
        const hasAllBreakdownKeys = breakdownKeys.every((key) => typeof d[key] === 'number');

        userStats = hasAllBreakdownKeys
          ? {
              total: (d.total as number) ?? 0,
              admin: (d.admin as number) ?? 0,
              free: (d.free as number) ?? 0,
              basic: (d.basic as number) ?? 0,
              standard: (d.standard as number) ?? 0,
              pro: (d.pro as number) ?? 0,
              basicIndividualPaid: (d.basicIndividualPaid as number) ?? 0,
              basicGroupCoupon: (d.basicGroupCoupon as number) ?? 0,
              basicEventCoupon: (d.basicEventCoupon as number) ?? 0,
              standardIndividualPaid: (d.standardIndividualPaid as number) ?? 0,
              standardGroupCoupon: (d.standardGroupCoupon as number) ?? 0,
              standardEventCoupon: (d.standardEventCoupon as number) ?? 0,
              proIndividualPaid: (d.proIndividualPaid as number) ?? 0,
              proGroupCoupon: (d.proGroupCoupon as number) ?? 0,
              proEventCoupon: (d.proEventCoupon as number) ?? 0,
            }
          : await buildStatsByScanningUsers();

        if (hasAllBreakdownKeys && planBreakdownMismatch(userStats)) {
          userStats = await buildStatsByScanningUsers();
        }
        const raw = d.lastUpdated;
        lastUpdated = raw != null
          ? (typeof raw === 'number' ? new Date(raw) : (raw as any)?.toDate?.() ?? new Date())
          : new Date();
      } else {
        // 집계 문서 없음: 클라이언트에서 전체 users 스캔 후 새 3분류 기준으로 집계
        userStats = await buildStatsByScanningUsers();
        lastUpdated = new Date();
      }

      return {
        success: true,
        data: {
          userStats,
          lastUpdated
        }
      };
    } catch (error) {
      console.error('통계 조회 실패:', error);
      return { 
        success: false, 
        error: `통계 조회에 실패했습니다: ${error}` 
      };
    }
  },

  /** 관리자 통계 블록별 사용자 목록 (Callable). 동일 세그먼트는 localStorage 캐시(TTL)로 재조회 시 서버 호출 안 함 */
  async listUsersForStatsSegment(
    _adminUserId: string,
    segment: string,
    options?: { bypassCache?: boolean }
  ): Promise<{ success: boolean; members?: AdminStatsListMember[]; error?: string; fromCache?: boolean }> {
    void _adminUserId;
    if (!options?.bypassCache) {
      const cached = readAdminStatsListLocalCache(segment);
      if (cached) {
        return { success: true, members: cached, fromCache: true };
      }
    }
    try {
      const fn = httpsCallable<{ segment: string }, { success: boolean; members?: AdminStatsListMember[]; error?: string }>(
        functions,
        'listUsersForStatsSegment'
      );
      const res = await fn({ segment });
      const data = res.data as { success?: boolean; members?: AdminStatsListMember[]; error?: string };
      if (data?.success && Array.isArray(data.members)) {
        const normalizedMembers = normalizeAdminStatsListMembers(data.members);
        writeAdminStatsListLocalCache(segment, normalizedMembers);
        return { success: true, members: normalizedMembers, fromCache: false };
      }
      return { success: false, error: data?.error || '목록을 불러오지 못했습니다.' };
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string; details?: unknown };
      console.error('통계 사용자 목록 조회 실패:', error);
      const hint =
        err?.code === 'functions/not-found'
          ? ' (함수를 찾지 못했습니다. 배포가 안 된 경우도 있고, 접속 주소(예: 127.0.0.1·LAN IP)가 이전에 CORS로 막혔을 수 있습니다. 공식 주소 https://luckytem-class-lab-00.web.app 로 접속하거나, 최신 Functions 배포 후 다시 시도해 주세요.)'
          : err?.code === 'internal' || err?.message === 'internal'
            ? ' (서버 오류: Functions 로그를 확인하거나 최신 Functions 배포가 필요할 수 있습니다.)'
            : '';
      return { success: false, error: `${err?.message || '목록을 불러오지 못했습니다.'}${hint}` };
    }
  },

  // 랜덤 문자 3개 생성 (대문자만)
  generateRandomLetters(): string {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < 3; i++) {
      result += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    return result;
  },

  // 랜덤 숫자 4개 생성
  generateRandomNumbers(): string {
    return Math.floor(1000 + Math.random() * 9000).toString();
  },

  // 새로운 코드 형식 생성 (B1Y-AAA1234)
  generateNewCodeFormat(prefix: string): string {
    const letters = this.generateRandomLetters();
    const numbers = this.generateRandomNumbers();
    return `${prefix}-${letters}${numbers}`;
  },

  // 이벤트 코드 생성 (선착순 활성화 가능, EVT- 접두사)
  async createEventCode(
    adminUserId: string,
    options: { targetUserType: 'basic' | 'standard' | 'pro'; durationMonths: number; maxActivations: number; name?: string }
  ): Promise<{ success: boolean; code?: string; error?: string }> {
    try {
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) {
        return { success: false, error: '관리자 권한이 필요합니다.' };
      }

      if (options.durationMonths <= 0 || options.maxActivations <= 0) {
        return { success: false, error: '이용기간과 활성 사용자 수는 1 이상이어야 합니다.' };
      }

      let code = '';
      let attempts = 0;
      while (attempts < 5) {
        attempts += 1;
        code = this.generateNewCodeFormat('EVT');
        const codeRef = doc(db, EVENT_CODES_COLLECTION, code);
        const codeSnap = await getDoc(codeRef);
        if (!codeSnap.exists()) {
          const now = Timestamp.now();
          await setDoc(codeRef, {
            code,
            name: (options.name || '').trim() || undefined,
            targetUserType: options.targetUserType,
            durationMonths: options.durationMonths,
            maxActivations: options.maxActivations,
            activatedCount: 0,
            activatedUsers: [],
            createdAt: now,
            createdBy: adminUserId,
            isClosed: false
          });
          return { success: true, code };
        }
      }

      return { success: false, error: '이벤트 코드 생성에 실패했습니다. 다시 시도해주세요.' };
    } catch (error) {
      console.error('이벤트 코드 생성 실패:', error);
      return { success: false, error: '이벤트 코드 생성에 실패했습니다.' };
    }
  },

  // 이벤트 코드 목록 조회 (관리자 전용)
  async getEventCodes(adminUserId: string): Promise<{ success: boolean; codes?: EventCode[]; error?: string }> {
    try {
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) {
        return { success: false, error: '관리자 권한이 필요합니다.' };
      }

      const snapshot = await getDocs(collection(db, EVENT_CODES_COLLECTION));
      const codes = snapshot.docs.map(docSnap => {
        const data = docSnap.data() as EventCode;
        return {
          ...data,
          code: data.code || docSnap.id,
          createdAt: (data.createdAt as any)?.toDate ? (data.createdAt as any).toDate() : data.createdAt,
          closedAt: (data.closedAt as any)?.toDate ? (data.closedAt as any).toDate() : data.closedAt
        };
      });

      codes.sort((a, b) => {
        const aTime = (a.createdAt as any)?.getTime ? (a.createdAt as any).getTime() : 0;
        const bTime = (b.createdAt as any)?.getTime ? (b.createdAt as any).getTime() : 0;
        return bTime - aTime;
      });

      return { success: true, codes };
    } catch (error) {
      console.error('이벤트 코드 목록 조회 실패:', error);
      return { success: false, error: '이벤트 코드 목록 조회에 실패했습니다.' };
    }
  },

  // 이벤트 코드 마감/재개
  async setEventCodeClosed(
    adminUserId: string,
    code: string,
    isClosed: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) {
        return { success: false, error: '관리자 권한이 필요합니다.' };
      }

      const codeRef = doc(db, EVENT_CODES_COLLECTION, code);
      await updateDoc(codeRef, {
        isClosed,
        closedAt: isClosed ? Timestamp.now() : null
      });

      return { success: true };
    } catch (error) {
      console.error('이벤트 코드 상태 변경 실패:', error);
      return { success: false, error: '이벤트 코드 상태 변경에 실패했습니다.' };
    }
  },

  // 이벤트 코드 삭제
  async deleteEventCode(
    adminUserId: string,
    code: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) {
        return { success: false, error: '관리자 권한이 필요합니다.' };
      }

      const codeRef = doc(db, EVENT_CODES_COLLECTION, code);
      await deleteDoc(codeRef);
      return { success: true };
    } catch (error) {
      console.error('이벤트 코드 삭제 실패:', error);
      return { success: false, error: '이벤트 코드 삭제에 실패했습니다.' };
    }
  },

  async getGroupCouponBatches(adminUserId: string): Promise<{ success: boolean; batches?: GroupCouponBatch[]; error?: string }> {
    try {
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) return { success: false, error: '관리자 권한이 필요합니다.' };

      const snapshot = await getDocs(collection(db, GROUP_COUPON_CODES_COLLECTION));
      const batches = snapshot.docs.map((docSnap) => mapGroupCouponBatch(docSnap.id, docSnap.data()));
      batches.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      return { success: true, batches };
    } catch (error) {
      console.error('그룹 이용권 배치 목록 조회 실패:', error);
      return { success: false, error: '그룹 이용권 배치 목록 조회에 실패했습니다.' };
    }
  },

  async refundGroupCouponBatch(adminUserId: string, batchId: string): Promise<{ success: boolean; error?: string; refundKrw?: number; unusedRefundKrw?: number; activatedRefundKrw?: number; refundedCount?: number }> {
    try {
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) return { success: false, error: '관리자 권한이 필요합니다.' };
      const refundFn = httpsCallable<{ groupCode: string }, { success: boolean; error?: string; refundKrw?: number; unusedRefundKrw?: number; activatedRefundKrw?: number; refundedCount?: number }>(functions, 'refundGroupCouponBatch');
      const res = await refundFn({ groupCode: batchId });
      const data = res.data as any;
      if (data?.success) return { success: true, refundKrw: data.refundKrw, unusedRefundKrw: data.unusedRefundKrw, activatedRefundKrw: data.activatedRefundKrw, refundedCount: data.refundedCount };
      return { success: false, error: data?.error || '그룹 이용권 환불에 실패했습니다.' };
    } catch (error: any) {
      console.error('그룹 이용권 환불 실패:', error);
      return { success: false, error: error?.message || '그룹 이용권 환불에 실패했습니다.' };
    }
  },

  async getMyGroupCouponBatches(userId: string): Promise<{ success: boolean; batches?: GroupCouponBatch[]; error?: string }> {
    try {
      const q = query(
        collection(db, GROUP_COUPON_CODES_COLLECTION),
        where('buyerUid', '==', userId)
      );
      const snapshot = await getDocs(q);
      const batches = snapshot.docs.map((docSnap) => mapGroupCouponBatch(docSnap.id, docSnap.data()));
      batches.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      return { success: true, batches };
    } catch (error) {
      console.error('내 그룹 이용권 배치 목록 조회 실패:', error);
      return { success: false, error: '그룹 이용권 구매 이력을 불러오지 못했습니다.' };
    }
  },

  async getGroupCouponRefundQuote(batchId: string): Promise<{
    success: boolean;
    error?: string;
    refundKrw?: number;
    unusedRefundKrw?: number;
    activatedRefundKrw?: number;
    unusedCount?: number;
    activatedRefundedCount?: number;
    totalAmountKrw?: number;
    unitPriceKrw?: number;
    quantity?: number;
  }> {
    try {
      const quoteFn = httpsCallable<
        { groupCode: string },
        { refundKrw: number; unusedRefundKrw: number; activatedRefundKrw: number; unusedCount: number; activatedRefundedCount: number; totalAmountKrw: number; unitPriceKrw: number; quantity: number }
      >(functions, 'getGroupCouponRefundQuote');
      const res = await quoteFn({ groupCode: batchId });
      const data = res.data as any;
      if (data?.refundKrw != null) {
        return {
          success: true,
          refundKrw: data.refundKrw,
          unusedRefundKrw: data.unusedRefundKrw,
          activatedRefundKrw: data.activatedRefundKrw,
          unusedCount: data.unusedCount,
          activatedRefundedCount: data.activatedRefundedCount,
          totalAmountKrw: data.totalAmountKrw,
          unitPriceKrw: data.unitPriceKrw,
          quantity: data.quantity,
        };
      }
      return { success: false, error: '환불 금액을 조회할 수 없습니다.' };
    } catch (error: any) {
      console.error('그룹 이용권 환불 견적 조회 실패:', error);
      return {
        success: false,
        error: error?.message || error?.code || '환불 견적 조회에 실패했습니다.',
      };
    }
  },

  async refundOwnGroupCouponBatch(batchId: string): Promise<{ success: boolean; error?: string; refundKrw?: number; refundedCount?: number; unusedRefundKrw?: number; activatedRefundKrw?: number; activatedRefundedCount?: number; unusedRefundedCount?: number }> {
    try {
      const refundFn = httpsCallable<{ groupCode: string }, { success: boolean; error?: string; refundKrw?: number; refundedCount?: number; unusedRefundKrw?: number; activatedRefundKrw?: number; activatedRefundedCount?: number; unusedRefundedCount?: number }>(
        functions,
        'refundGroupCouponBatch'
      );
      const res = await refundFn({ groupCode: batchId });
      const data = res.data as any;
      if (data?.success) return {
        success: true,
        refundKrw: data.refundKrw,
        refundedCount: data.refundedCount,
        unusedRefundKrw: data.unusedRefundKrw,
        activatedRefundKrw: data.activatedRefundKrw,
        activatedRefundedCount: data.activatedRefundedCount,
        unusedRefundedCount: data.unusedRefundedCount,
      };
      return { success: false, error: data?.error || '그룹 이용권 환불에 실패했습니다.' };
    } catch (error: any) {
      console.error('그룹 이용권 환불 실패:', error);
      return { success: false, error: error?.message || '그룹 이용권 환불에 실패했습니다.' };
    }
  },

  async resendGroupCouponBatchEmail(batchId: string, targetEmail?: string): Promise<{ success: boolean; error?: string; resentCount?: number }> {
    try {
      const groupCode = (batchId || '').trim();
      if (!groupCode) return { success: false, error: 'groupCode가 필요합니다.' };
      const resendFn = httpsCallable<{ groupCode: string; targetEmail?: string }, { success: boolean; error?: string; resentCount?: number }>(
        functions,
        'resendGroupCouponBatchEmail'
      );
      const res = await resendFn({ groupCode, targetEmail });
      const data = res.data as any;
      if (data?.success) return { success: true, resentCount: data.resentCount };
      return { success: false, error: data?.error || '쿠폰 재발송에 실패했습니다.' };
    } catch (error: any) {
      console.error('그룹 쿠폰 재발송 실패:', error);
      return { success: false, error: error?.message || '쿠폰 재발송에 실패했습니다.' };
    }
  },

  // 권한별 사용자 목록 조회
  async getUsersByType(
    adminUserId: string,
    targetUserType: 'basic' | 'standard' | 'pro'
  ): Promise<{
    success: boolean;
    data?: Array<{
      email: string;
      name: string;
      upgradeCode?: string;
      usedAt?: Date;
    }>;
    error?: string;
  }> {
    try {
      // 관리자 권한 확인
      const isAdmin = await this.checkAdminPermission(adminUserId);
      if (!isAdmin) {
        return { success: false, error: '관리자 권한이 필요합니다.' };
      }

      // 해당 권한을 가진 사용자 찾기
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('userType', '==', targetUserType)
      );

      const usersSnapshot = await getDocs(q);
      
      if (usersSnapshot.empty) {
        return {
          success: true,
          data: []
        };
      }

      const users = usersSnapshot.docs.map(doc => {
        const userData = doc.data();
        return {
          email: userData.email || '알 수 없음',
          name: userData.name || '알 수 없음',
          upgradeCode: userData.upgradeCode?.code,
          usedAt: userData.upgradeCode?.usedAt instanceof Date ? 
            userData.upgradeCode.usedAt : 
            new Date((userData.upgradeCode?.usedAt as any)?.seconds * 1000 || 0)
        };
      }).sort((a, b) => {
        // 이메일 순으로 정렬
        return a.email.localeCompare(b.email);
      });

      return {
        success: true,
        data: users
      };
    } catch (error) {
      console.error('사용자 목록 조회 실패:', error);
      return {
        success: false,
        error: `사용자 목록 조회에 실패했습니다: ${error}`
      };
    }
  },

};